<template>
  <div class="p-4">
    <h2 class="pb-4 font-medium text-gray-800">{{ $t('pages.myAccount') }}</h2>
    <div class="pt-4">
      <div class="flex items-center pb-4">
        <img class="mr-3 w-10 h-10 rounded-full" src="@/assets/images/profile-pic.svg" />
        <h2 class="font-semibold text-gray-700">Infosupport</h2>
      </div>
      <table v-if="sessionDetails !== undefined" class="text-xs">
        <tbody>
          <tr>
            <td class="py-2 pr-8 font-semibold capitalize">{{ $t('auth.username') }}</td>
            <td>{{ sessionDetails.userName }}</td>
          </tr>
          <tr>
            <td class="py-2 pr-8 font-semibold">{{ $t('auth.userType') }}</td>
            <td>{{ sessionDetails.userType }}</td>
          </tr>
          <tr>
            <td class="py-2 pr-8 font-semibold">{{ $t('device.lastLogin') }}</td>
            <td>{{ lastLogin }}</td>
          </tr>
        </tbody>
      </table>
      <div class="mt-6 border-t border-gray-300 w-[50%]"></div>
      <div class="py-4">{{ $t('auth.resetPassword') }}</div>
      <div>
        <label for="email" class="text-sm font-medium">{{ $t('auth.email') }}</label>
        <BaseInput
          v-model="userEmail"
          type="email"
          color="base"
          class="my-1 w-full sm:w-[50%] lg:w-[35%]"
          :error="$v.userEmail.$invalid ? $v.userEmail.required.$message : ''">
        </BaseInput>
      </div>
      <div class="mt-4">
        <label for="username" class="text-sm font-medium capitalize">{{
          $t('auth.username')
        }}</label>
        <BaseInput
          outline
          v-model="username"
          type="text"
          color="base"
          class="my-1 w-full sm:w-[50%] lg:w-[35%] text-gray-800"
          :error="$v.username.required.$invalid ? $v.username.required.$message : ''">
        </BaseInput>
      </div>
      <BaseButton
        outline
        class="mt-4 w-48 text-white border-0 bg-theme-600"
        @click="onResetPassword"
        >{{ $t('common.actions.save') }}</BaseButton
      >
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import type { SessionDetails } from '@/types/Base';
import { isoDateToFullDateTime } from '@/utils/date';
import { resetPassword } from '@/services/api/auth';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useToastStore } from '@/stores/toast';
import { required, helpers, email } from '@vuelidate/validators';

const sessionDetails = ref<SessionDetails | undefined>();
const lastLogin = ref<string | undefined>('');
const username = ref('');
const userEmail = ref('');
const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });
onMounted(() => {
  const memorySession = localStorage.getItem('sessionDetails');
  if (memorySession) sessionDetails.value = JSON.parse(memorySession) as SessionDetails;
  if (sessionDetails.value) lastLogin.value = isoDateToFullDateTime(sessionDetails.value.lastAlive);
});
const rules = computed(() => ({
  username: { required: helpers.withMessage('Username is required', required) },
  userEmail: {
    required: helpers.withMessage('Invalid email', required),
    email: helpers.withMessage('Invalid email', email),
  },
}));
const $v = useVuelidate(rules, { username, userEmail }, { $lazy: true });
async function onResetPassword() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (sessionDetails.value) {
    const result = await resetPassword(
      userEmail.value,
      username.value,
      sessionDetails.value.dbName
    );
    if (result) {
      toastStore.success({
        timeout: 5000,
        text: `${$t('messages.success.resetPassword')} ${result}`,
      });
    }
  }
}
</script>
